import {
  inspectPlanningBacklogList,
  backlogListResolveErrorMessage,
  backlogListNeedsReinspection,
  type BacklogListInspection,
  type BacklogListImportOptions,
  type BacklogDurationStrategy,
} from "../../../../../../../../../../../../../../../../../api/kehrblatt/backlog-lists"
import { euroInputCents } from "../../../../../_shared/_logic/backlog-estimation"
import { summedTypicalDurationMinutes } from "../../../../../../../../../../../../../../../_shared/_logic/order-type-selection"
import { MAX_PREPARATION_DURATION_MINUTES } from "../../../../../../../_shared/_logic/work-order-creation"

type ImportInput = {
  organizationId: () => string
  orderTypes: () => OrderType[]
  onConfirm: (
    file: File,
    options: BacklogListImportOptions,
    signal: AbortSignal,
  ) => Promise<boolean> | boolean
  onApplied: () => void
  initialStrategy?: BacklogDurationStrategy
}

/** Own the file and choices until a current, successful response is explicitly applied. */
export const createImportDraft = (input: ImportInput) => {
  const state = $state({
    file: null as File | null,
    inspection: null as BacklogListInspection | null,
    step: 0,
    inspecting: false,
    submitting: false,
    error: null as string | null,
    automatic: false,
    selectedKeys: [] as string[],
    selectedIds: [] as string[],
    fixedDuration: null as string | null,
    durationMethod: "typical" as "typical" | "price",
    hourlyRate:
      input.initialStrategy?.method === "price"
        ? String(input.initialStrategy.hourly_rate_cents / 100)
        : "",
    minimumPrice:
      input.initialStrategy?.method === "price"
        ? String(input.initialStrategy.minimum_price_cents / 100)
        : "20,00",
  })
  let active = true
  let requestId = 0
  let inspectionRequest: AbortController | null = null
  let submissionRequest: AbortController | null = null
  const automaticAvailable = () =>
    Boolean(state.inspection?.detected_work_types.length) &&
    ["portal_uno_office", "portal_kamin_futura"].includes(state.inspection?.source_system ?? "")
  const fileValid = () =>
    Boolean(
      state.file &&
      state.inspection?.source_system &&
      !state.inspection.errors.length &&
      !state.inspecting,
    )
  const priceAvailable = () =>
    Boolean(state.inspection?.price_supported && state.inspection.priced_row_count)
  const usesCommonPreset = () =>
    Boolean(state.inspection?.source_system) &&
    !["portal_uno_office", "portal_kamin_futura"].includes(state.inspection?.source_system ?? "")
  const presetDuration = (): string => {
    if (state.fixedDuration !== null) return state.fixedDuration

    const selected = [...new Set(state.selectedIds)]
    const types = input.orderTypes()

    if (
      !selected.length ||
      selected.some((id) => {
        const duration = Number(types.find((type) => type.id === id)?.typical_duration_min)

        return !Number.isFinite(duration) || duration <= 0
      })
    )
      return ""

    return String(summedTypicalDurationMinutes(selected, types) ?? "")
  }
  const options = (): BacklogListImportOptions | null => {
    if (!fileValid() || !state.inspection) return null

    const identity = {
      document_sha256: state.inspection.document_sha256,
      contract_version: 2 as const,
      evaluation_date: state.inspection.evaluation_date,
    }
    let duration_strategy: BacklogDurationStrategy = { method: "typical" }

    if (usesCommonPreset()) {
      const minutes = Number(presetDuration())

      if (
        !Number.isInteger(minutes) ||
        minutes < 5 ||
        minutes > MAX_PREPARATION_DURATION_MINUTES ||
        minutes % 5
      )
        return null

      duration_strategy = { method: "fixed", duration_minutes: minutes }
    } else if (state.durationMethod === "price") {
      const hourly = euroInputCents(state.hourlyRate)
      const minimum = euroInputCents(state.minimumPrice)

      if (!priceAvailable() || hourly === null || hourly <= 0 || minimum === null) return null

      duration_strategy = {
        method: "price",
        hourly_rate_cents: hourly,
        minimum_price_cents: minimum,
      }
    }

    if (state.automatic) {
      const offered = new Set(state.inspection.detected_work_types.map((item) => item.key))
      const catalogKeys = new Set(input.orderTypes().map((item) => item.key))

      if (
        !automaticAvailable() ||
        !state.selectedKeys.length ||
        state.selectedKeys.some((key) => !offered.has(key) || !catalogKeys.has(key))
      )
        return null

      return {
        ...identity,
        mode: "automatic",
        order_type_keys: [...state.selectedKeys],
        duration_strategy,
      }
    }

    const catalogIds = new Set(input.orderTypes().map((item) => item.id))

    if (!state.selectedIds.length || state.selectedIds.some((id) => !catalogIds.has(id)))
      return null

    return {
      ...identity,
      mode: "manual",
      order_type_ids: [...state.selectedIds],
      duration_strategy,
    }
  }

  const draft = {
    state,
    automaticAvailable,
    priceAvailable,
    usesCommonPreset,
    presetDuration,
    setPresetDuration: (value: unknown) => {
      state.fixedDuration = String(value ?? "")
    },
    fileValid,
    options,
    async selectFile(file: File) {
      const id = ++requestId
      inspectionRequest?.abort()
      submissionRequest?.abort()
      inspectionRequest = new AbortController()
      const request = inspectionRequest
      const organizationId = input.organizationId()
      state.file = file
      state.inspection = null
      state.error = null
      state.step = 0
      state.automatic = false
      state.selectedKeys = []
      state.selectedIds = []
      state.fixedDuration = null
      state.durationMethod = "typical"
      state.submitting = false
      state.inspecting = true
      try {
        const report = await inspectPlanningBacklogList({
          organizationId,
          file,
          signal: request.signal,
        })

        if (
          !active ||
          request.signal.aborted ||
          id !== requestId ||
          organizationId !== input.organizationId()
        )
          return

        state.inspection = report
        state.automatic = automaticAvailable()
        state.selectedKeys = report.detected_work_types.map((item) => item.key)
        state.durationMethod = priceAvailable() ? "price" : "typical"

        if (usesCommonPreset()) {
          const sweeping = input.orderTypes().find((type) => type.key === "system.sweeping")
          state.selectedIds = sweeping ? [sweeping.id] : []
        }
      } catch (error) {
        if (active && !request.signal.aborted && id === requestId)
          state.error = backlogListResolveErrorMessage(error)
      } finally {
        if (active && id === requestId) state.inspecting = false
      }
    },
    selectManualTypes(value: unknown) {
      state.selectedIds = Array.isArray(value) ? [...new Set(value.map(String))] : []
    },
    rejectFiles() {
      requestId++
      inspectionRequest?.abort()
      submissionRequest?.abort()
      state.file = null
      state.inspection = null
      state.inspecting = false
      state.submitting = false
      state.step = 0
      state.error = "Bitte wählen Sie genau eine Datei."
    },
    async submit(): Promise<void> {
      const choice = options()

      if (!choice || !state.file || state.submitting) return

      const id = requestId
      const request = new AbortController()
      submissionRequest = request
      state.submitting = true
      state.error = null
      try {
        const applied = await input.onConfirm(state.file, choice, request.signal)

        if (active && !request.signal.aborted && id === requestId && applied) input.onApplied()
      } catch (error) {
        if (active && !request.signal.aborted && id === requestId) {
          if (backlogListNeedsReinspection(error) && state.file) {
            await draft.selectFile(state.file)

            return
          }

          state.error = backlogListResolveErrorMessage(
            error,
            state.inspection?.source_system ?? undefined,
          )
        }
      } finally {
        if (active && id === requestId) state.submitting = false
      }
    },
    dispose() {
      active = false
      requestId++
      inspectionRequest?.abort()
      submissionRequest?.abort()
    },
  }

  return draft
}

export type ImportDraft = ReturnType<typeof createImportDraft>
